import React, { useEffect, useMemo, useRef, useState } from 'react';
import { RouteComponentProps, withRouter, useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import RestService from '../Services/RestService';
import OKomBreadcrumb from '../Components/OsloKommuneUI/OKomBreadcrumb';
import OKomLoading from '../Components/OsloKommuneUI/OKomLoading';
import { useUserContext } from '../Components/StateContext/UserContext';
import myconfig from '../Config';

const PREVIEW_W = 480;
const PREVIEW_H = 680;   // ~A4 portrait at 480px wide

// Small first-page thumbnail, fetched through the authenticated proxy (/api/latex/Thumbnail/{id})
// and shown via an object URL — the blob is in a private container so we can't <img src> it
// directly. `version` (UpdatedDate) busts the cache. Renders nothing until/unless one exists.
// Hovering shows a larger floating preview of the same image.
const Thumbnail: React.FC<{ docId: number; version?: string; directUrl?: string }> = ({ docId, version, directUrl }) => {
  const [url, setUrl] = useState<string | null>(directUrl ?? null);
  const [preview, setPreview] = useState<{ top: number; left: number } | null>(null);
  const wrapRef = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    // Preferred path: load the thumbnail directly from Azure (read-only SAS) — no proxy fetch.
    if (directUrl) { setUrl(directUrl); return; }

    // Fallback (no read SAS configured): stream it through the authenticated API proxy.
    let objectUrl: string | null = null;
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('myToken');
        const v = version ? `?v=${encodeURIComponent(version)}` : '';
        const res = await fetch(`${myconfig.rootUrl}/api/latex/Thumbnail/${docId}${v}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) return;
        const blob = await res.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      } catch { /* no thumbnail yet */ }
    })();
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [docId, version, directUrl]);

  // Position the floating preview next to the thumbnail, flipping/clamping to stay on screen.
  const showPreview = () => {
    const r = wrapRef.current?.getBoundingClientRect();
    if (!r) return;
    let left = r.right + 8;
    if (left + PREVIEW_W > window.innerWidth) left = Math.max(8, r.left - 8 - PREVIEW_W);
    let top = r.top;
    if (top + PREVIEW_H > window.innerHeight) top = Math.max(8, window.innerHeight - PREVIEW_H - 8);
    setPreview({ top, left });
  };

  if (!url) return null;
  return (
    <span
      ref={wrapRef}
      style={{ display: 'inline-block', flexShrink: 0 }}
      onMouseEnter={showPreview}
      onMouseLeave={() => setPreview(null)}
    >
      <img
        src={url}
        alt=""
        style={{ width: 64, height: 'auto', border: '1px solid #ddd', borderRadius: 2, display: 'block' }}
      />
      {preview && (
        <div style={{
          position: 'fixed', top: preview.top, left: preview.left, zIndex: 1000,
          background: '#fff', border: '1px solid #ccc', borderRadius: 4, padding: 4,
          boxShadow: '0 6px 24px rgba(0,0,0,0.3)', pointerEvents: 'none',
        }}>
          <img src={url} alt="" style={{ width: PREVIEW_W, height: 'auto', display: 'block' }} />
        </div>
      )}
    </span>
  );
};

// "12.03.2026 14:05" — date + time in Norwegian locale; empty for missing/invalid values.
const formatDate = (value?: string): string => {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('nb-NO') + ' ' + d.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' });
};

const DokumenterView: React.FC<RouteComponentProps> = () => {
  const history = useHistory();
  const location = useLocation<{ highlightId?: number } | undefined>();
  const highlightId = location.state?.highlightId;
  const highlightRef = useRef<HTMLDivElement | null>(null);
  const { user } = useUserContext();

  const [isLoading, setIsLoading] = useState(false);
  const [docs, setDocs] = useState<Api.LatexDocumentListItem[]>([]);
  const [query, setQuery] = useState('');
  const [deletingId, setDeletingId] = useState<number | null>(null);

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const all = await RestService.get<Api.LatexDocumentListItem[]>('/api/latex/All');
        setDocs(all ?? []);
      } catch (e) {
        toast.error(`Kunne ikke laste dokumenter: ${e}`);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  // Bring the freshly duplicated card into view once the list has loaded.
  useEffect(() => {
    if (highlightId != null && highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlightId, docs]);

  // Quick filter across name + creator/editor names.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return docs;
    return docs.filter(d =>
      d.Name.toLowerCase().includes(q) ||
      (d.CreatedByName ?? '').toLowerCase().includes(q) ||
      (d.ModifiedByName ?? '').toLowerCase().includes(q));
  }, [docs, query]);

  const openDoc = (id: number) => history.push(`/latex/${id}`);

  // Delete is owner-only (also enforced server-side). The button lives inside the card's
  // <a>, so swallow the click so it doesn't also navigate into the editor.
  const deleteDoc = async (e: React.MouseEvent, doc: Api.LatexDocumentListItem) => {
    e.preventDefault();
    e.stopPropagation();
    if (deletingId != null) return;
    if (!window.confirm(`Slette «${doc.Name}»? Dette kan ikke angres.`)) return;
    try {
      setDeletingId(doc.Id);
      await RestService.del(`/api/latex/Delete/${doc.Id}`);
      setDocs(prev => prev.filter(d => d.Id !== doc.Id));
      toast.success('Dokumentet ble slettet.');
    } catch (err) {
      toast.error(`Kunne ikke slette: ${err}`);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      <OKomBreadcrumb path='Dokumenter' />
      {isLoading && <OKomLoading text='Henter dokumenter …' />}

      <div className="ods-margin-top-11 ods-grid ods-grid--center">
        <div className="ods-grid__column--12 ods-padding-horizontal-5">
          <h1 className='ods-text--align-center ods-text--size-kilo ods-text--size-echo-breakpoint-medium'>Dokumenter</h1>

          <div className="ods-search-inline ods-margin-top-3">
            <label className="ods-search-inline__label">
              <input
                placeholder='Søk på navn eller person'
                className="ods-search-inline__input"
                type="search"
                autoComplete="off"
                value={query}
                onChange={e => setQuery(e.target.value)}
              />
            </label>
          </div>

          <div className="ods-margin-top-3 ods-grid ods-grid--gap">
            {filtered.map(d => (
              <div key={d.Id} className="ods-grid__column--12 ods-grid__column--4-breakpoint-medium">
                <div
                  ref={d.Id === highlightId ? highlightRef : undefined}
                  className={`ods-landscape-card ods-landscape-card--no-image${d.Id === highlightId ? ' okom-flash-row' : ''}`}
                >
                  <a
                    className="ods-landscape-card__link"
                    href={`/latex/${d.Id}`}
                    onClick={e => { e.preventDefault(); openDoc(d.Id); }}
                  >
                    <div className="ods-landscape-card__content">

                      <h3 className="ods-landscape-card__heading">{d.Name}</h3>
                      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                        <Thumbnail docId={d.Id} version={d.UpdatedDate} directUrl={d.ThumbnailUrl} />
                        <div>
                          <p className="ods-landscape-card__published">Sist endret: {formatDate(d.UpdatedDate)}</p>
                          <p className="ods-landscape-card__published">{d.CreatedByName || 'Ukjent eier'}</p>
                        </div>
                      </div>
                      {user?.Id != null && d.CreatedBy === user.Id && (
                        <div style={{ marginTop: '0.75rem', textAlign: 'right' }}>
                          <button
                            type="button"
                            className="ods-button ods-button--red"
                            style={{ padding: '0.25rem 0.75rem', fontSize: '0.8rem' }}
                            disabled={deletingId === d.Id}
                            onClick={e => deleteDoc(e, d)}
                          >
                            Slett
                          </button>
                        </div>
                      )}
                    </div>
                  </a>
                </div>
              </div>
            ))}

            {filtered.length === 0 && !isLoading && (
              <div className="ods-grid__column--12">
                <p className="ods-text--align-center ods-color-text-grayscale-10">
                  {docs.length === 0 ? 'Ingen dokumenter.' : 'Ingen treff.'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default withRouter(DokumenterView);
